import { SkalpelRoot } from '@skalpel/components';
import React from 'react';
import { createPortal, flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';

import Calendar from './Calendar/Calendar';
import Graph from './Graph/Graph';
import Listeners from './Listeners/Listeners';
import ModalWindow from './ModalWindow/ModalWindow';
import ErrorBoundary from './ReactComponents/Components/ErrorBoundary';
import { QueryProvider } from './ReactComponents/Components/QueryProvider';
import Stats from './Stats/Stats';
import Table from './Table/Table';
import { isInProduction } from './utils/isInProduction';
import Webs from './Wizard/Webs';
import ZoneBanner from './Wizard/ZoneBanner';
import Zones from './Wizard/Zones';

const reactComponents = {
	ArticleRequestWizard: () => import('./ReactComponents/ArticleRequestWizard/ArticleRequestWizard'),
	ArticlesRequestOverview: () => import('./ReactComponents/ArticlesRequestOverview/ArticlesRequestOverview'),
	ArticlesStatisticsOverview: () => import('./ReactComponents/ArticlesStatistics/overview/ArticlesStatisticsOverview'),
	ArticleStatisticsDetail: () => import('./ReactComponents/ArticlesStatistics/detail/ArticleStatisticsDetail'),
	BannedUser: () => import('./ReactComponents/StatusPage/user/BannedUser'),
	BaseHeader: () => import('./ReactComponents/Layout/BaseHeader'),
	CodesOverview: () => import('./ReactComponents/CodesOverview/CodesOverview'),
	ErrorPage: () => import('./ReactComponents/StatusPage/error/ErrorPage'),
	ExamplePage: () => import('./ReactComponents/ExamplePage/ExamplePage'),
	Footer: () => import('./ReactComponents/Layout/Footer'),
	GdprRemoved: () => import('./ReactComponents/StatusPage/user/GdprRemoved'),
	Header: () => import('./ReactComponents/Layout/Header'),
	HpfeedRequestCreation: () => import('./ReactComponents/HpfeedRequestWizard/HpfeedRequestWizard'),
	HpfeedRequestOverview: () => import('./ReactComponents/HpfeedRequestOverview/HpfeedRequestOverview'),
	HpFeedStatusCard: () => import('./ReactComponents/ContentService/HpFeedStatusCard'),
	IdentityRedirectOverview: () => import('./ReactComponents/IdentityRedirect/IdentityRedirectOverview'),
	MediumStatusCard: () => import('./ReactComponents/ContentService/MediumStatusCard'),
	MissingEmail: () => import('./ReactComponents/StatusPage/user/MissingEmail'),
	PageError: () => import('./ReactComponents/Components/PageError'),
	PageNotifications: () => import('./ReactComponents/common/notifications/PageNotifications'),
	PartnerStatusCard: () => import('./ReactComponents/ContentService/PartnerStatusCard'),
	PodcastStatisticsDetail: () => import('./ReactComponents/PodcastOverview/detail/PodcastStatisticsDetail'),
	PodcastStatisticsOverview: () => import('./ReactComponents/PodcastOverview/overview/PodcastStatisticsOverview'),
	PodcastStatusCard: () => import('./ReactComponents/ContentService/PodcastStatusCard'),
	ReportCreation: () => import('./ReactComponents/ReportCreation/ReportCreation'),
	RssValidator: () => import('./ReactComponents/RssValidator/RssValidator'),
	SeznamDoporucuje: () => import('./ReactComponents/SeznamDoporucuje/SeznamDoporucuje'),
	Sidebar: () => import('./ReactComponents/Layout/Sidebar'),
	StreamStatisticsDetail: () => import('./ReactComponents/StreamOverview/detail/StreamStatisticsDetail'),
	StreamStatisticsOverview: () => import('./ReactComponents/StreamOverview/overview/StreamStatisticsOverview'),
	StreamStatusCard: () => import('./ReactComponents/ContentService/StreamStatusCard'),
	ZoneOverview: () => import('./ReactComponents/ZoneOverview/ZoneOverview'),
};

void import('./sentryInit').then(({ default: initSentry }) => initSentry()).catch(() => {});

if (isInProduction()) {
	void import('./posthogInit')
		.then(({ default: initPostHog, registerPosthogUserData }) =>
			initPostHog().then(() => window.SSP && registerPosthogUserData(window.SSP)),
		)
		.catch(() => {});
}

require('chart.js');
require('./libs/color-picker.js');
require('../css/styles.less');
require('../css/globals.css');

function parseReactProps(element) {
	const propsJson = element.dataset.reactProps;

	if (!propsJson) {
		return {};
	}

	try {
		return JSON.parse(propsJson);
	} catch {
		return {};
	}
}

// NOTE(patrik.kaura): server-rendered + React portal architecture
// 1. server renders placeholders with data-react-component attributes in Jinja2 templates
// 2. Client finds all placeholders, dynamically imports components, and creates a single React root at #react-root
// 3. createPortal() teleports each component from #react-root to its placeholder div
// 4. all components share QueryProvider context despite rendering in different DOM locations
const mountReactComponents = async () => {
	const elements = document.querySelectorAll('[data-react-component]');

	const resolved = await Promise.all(
		Array.from(elements).reduce((acc, element) => {
			const componentImport = reactComponents[element.dataset.reactComponent];

			if (!componentImport) {
				return acc;
			}

			acc.push(
				(async () => {
					const { default: Component } = await componentImport();
					return {
						addErrorBoundary: element.dataset.errorBoundary !== 'false',
						Component,
						element,
						props: parseReactProps(element),
					};
				})(),
			);

			return acc;
		}, []),
	);

	const reactRootElement = document.getElementById('react-root');

	if (!reactRootElement) {
		return;
	}

	const reactRoot = createRoot(reactRootElement);

	flushSync(() => {
		reactRoot.render(
			<SkalpelRoot portalClassName="skalpel-reset">
				<QueryProvider>
					{resolved.map(({ element, Component, addErrorBoundary, props }, index) => {
						const content = addErrorBoundary ? (
							<ErrorBoundary>
								<Component {...props} />
							</ErrorBoundary>
						) : (
							<Component {...props} />
						);
						// eslint-disable-next-line typescript-eslint/restrict-template-expressions
						return createPortal(content, element, `portal-${index}`);
					})}
					<Toaster containerClassName="skalpel-reset" position="top-center" />
				</QueryProvider>
			</SkalpelRoot>,
		);
	});
};

function initVanillaComponents() {
	/* eslint-disable no-new */
	new Listeners();
	new ModalWindow();
	Calendar.init();
	new Graph();
	new Table();
	new Webs();
	new Zones();
	new ZoneBanner();
	new Stats();
	/* eslint-enable no-new */
}

(async () => {
	await mountReactComponents();

	const pageElement = document.querySelector('.Page');

	if (pageElement) {
		pageElement.classList.remove('hidden');
	}

	initVanillaComponents();
})();
